import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { motion } from 'framer-motion';
import { 
  Truck, Building2, HardHat, Warehouse,
  Wrench, Search, Sparkles, Cog, Wind, RefreshCw,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export const Services = () => {
  const { t } = useLanguage();

  const industrialServices = [
    { icon: Truck, title: t('title', translations.industrialServices.transportation), description: t('description', translations.industrialServices.transportation) },
    { icon: Building2, title: t('title', translations.industrialServices.facilitySupport), description: t('description', translations.industrialServices.facilitySupport) },
    { icon: HardHat, title: t('title', translations.industrialServices.construction), description: t('description', translations.industrialServices.construction) },
    { icon: Warehouse, title: t('title', translations.industrialServices.warehouse), description: t('description', translations.industrialServices.warehouse) },
  ];

  const technicalServices = [
    { icon: Wrench, title: t('title', translations.marineServices.maintenance), description: t('description', translations.marineServices.maintenance) },
    { icon: Search, title: t('title', translations.marineServices.inspection), description: t('description', translations.marineServices.inspection) },
    { icon: Sparkles, title: t('title', translations.marineServices.cleaning), description: t('description', translations.marineServices.cleaning) },
    { icon: Cog, title: t('title', translations.marineServices.pump), description: t('description', translations.marineServices.pump) },
    { icon: Wind, title: t('title', translations.marineServices.hvac), description: t('description', translations.marineServices.hvac) },
    { icon: RefreshCw, title: t('title', translations.marineServices.refurbishment), description: t('description', translations.marineServices.refurbishment) },
  ];

  return (
    <div data-testid="services-page" className="bg-[#0f1419]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="services-header">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1768128834332-7d3479c8d634?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxvaWwlMjByZWZpbmVyeSUyMHBpcGVzJTIwaW5kdXN0cmlhbCUyMHBsYW50JTIwZGFya3xlbnwwfHx8fDE3NzMwNzQ1Mzl8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419] via-[#0f1419]/95 to-[#0f1419]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">OUR EXPERTISE</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="services-title">
              {t('title', translations.servicesPage)}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl">
              {t('subtitle', translations.servicesPage)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industrial & Logistics - PRIMARY */}
      <section className="py-20 lg:py-28 border-t border-gray-800/50" data-testid="industrial-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="mb-16">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl lg:text-8xl font-bold text-gray-800">01</span>
              <div>
                <p className="text-blue-400 text-xs font-mono tracking-wider mb-1">PRIMARY SERVICES</p>
                <h2 className="text-3xl lg:text-4xl font-bold text-white" data-testid="industrial-title">
                  {t('industrialTitle', translations.servicesPage)}
                </h2>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {industrialServices.map((service, i) => (
              <motion.div
                key={i}
                {...fadeIn}
                className="group p-8 bg-[#0a0d10] border border-gray-800 hover:border-blue-600/50 transition-all"
                data-testid={`industrial-service-${i}`}
              >
                <div className="w-14 h-14 bg-blue-600/10 border border-blue-600/30 group-hover:bg-blue-600 group-hover:border-blue-600 transition-all flex items-center justify-center mb-6">
                  <service.icon className="w-6 h-6 text-blue-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Break */}
      <section className="relative h-[40vh] lg:h-[50vh]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1741655262435-4890ab9918fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxmb3JrbGlmdCUyMHdhcmVob3VzZSUyMGludGVyaW9yJTIwaW5kdXN0cmlhbCUyMGxvZ2lzdGljc3xlbnwwfHx8fDE3NzMwNzQ1NDR8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-[#0f1419]/60" />
      </section>

      {/* Technical Services - SECONDARY */}
      <section className="py-20 lg:py-28 bg-[#0a0d10] border-t border-gray-800/50" data-testid="marine-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="mb-16">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl lg:text-8xl font-bold text-gray-800">02</span>
              <div>
                <p className="text-gray-500 text-xs font-mono tracking-wider mb-1">TECHNICAL EXPERTISE</p>
                <h2 className="text-3xl lg:text-4xl font-bold text-white" data-testid="marine-title">
                  {t('marineTitle', translations.servicesPage)}
                </h2>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalServices.map((service, i) => (
              <motion.div
                key={i}
                {...fadeIn}
                className="group p-8 bg-[#0f1419] border border-gray-800 hover:border-gray-700 transition-all"
                data-testid={`marine-service-${i}`}
              >
                <div className="w-12 h-12 bg-gray-800 group-hover:bg-gray-700 transition-colors flex items-center justify-center mb-6">
                  <service.icon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-blue-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Need a Custom Solution?
              </h2>
              <p className="text-blue-100 text-lg max-w-xl">
                Contact us to discuss your specific operational requirements.
              </p>
            </div>
            <Button
              asChild
              className="bg-white text-blue-600 hover:bg-gray-100 rounded-none px-10 h-14 text-sm font-semibold group"
              data-testid="services-cta"
            >
              <Link to="/contact">
                Get in Touch
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
